/**
 * Helpers: Accessibility
 * ------------------------------------------------------------------------------
 * - Basic accessibility helpers.
 */

/**
 * Handle key presses.
 * @param {Event} event
 */
function handleKeyDown(event) {

  /**
   * Only update state when the tab key is pressed.
   */
  if (event.keyCode === 9) {
    setKeyboardNavigation(true)
  }
}

/**
 * Handle mouse clicks and touch events.
 */
function handleMouseDown() {
  setKeyboardNavigation(false)
}

/**
 * Set a class on the body element and update the window object flag when
 * keyboard or mouse navigation is detected.
 * @param {Boolean} state
 */
function setKeyboardNavigation(state = false) {
  document.body.classList.toggle('is-keyboard-user', state)
  window.mint.accessibility.keyboardNavigation = state
}

/**
 * Set event listeners for mouse/touchscreen clicks and key presses.
 */
export function detectKeyboardNavigation() {
  window.addEventListener('mousedown', handleMouseDown, true)
  window.addEventListener('keydown', handleKeyDown, true)
}

/**
 * Returns all focusable elements within a given container.
 * - Focusable elements include links, buttons, inputs and other interactive
 * elements.
 * - Inputs, textareas and selects are listed first to support natural focus
 * behavior.
 *
 * @param {HTMLElement} container - The container element to search within.
 * @returns {HTMLElement[]} - An array of focusable elements within the container.
 */
export function getFocusableElements(container) {
  if (!(container instanceof HTMLElement)) {
    return []
  }

  return Array.from(
    container.querySelectorAll(
      'input:not([type="hidden"]):enabled, textarea:enabled, select:enabled, button:enabled, a[href], summary, [tabindex]:not([tabindex^="-"]), [draggable], area, object, iframe',
    ),
  )
}

/**
 * Focuses the first focusable element within a given container.
 * - If the element is a text input or textarea, the cursor is placed at the end
 * of any pre-filled value.
 *
 * @param {HTMLElement} container - The container element to search within.
 */
export function focusFirstFocusableElement(container) {
  if (!(container instanceof HTMLElement)) {
    return
  }

  const focusableElements = getFocusableElements(container)

  if (focusableElements.length === 0) {
    return
  }

  const target = focusableElements[0]

  if (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement
  ) {
    const length = target.value.length
    target.setSelectionRange(length, length)
  }

  target.focus()
}
